import React, { useState, useMemo } from 'react';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/utils/utils';
import { UserSelectorEmployee } from './UserSelectorItem';
import { UserSelectorAvatar } from './UserSelectorAvatar';
import { ChooseListInput } from '@/shared/components/inputs/ChooseList.input';
import { Card, CardContent, CardHeader } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Button } from '@/shared/components/ui/Button';
import { EmptyStateIllustration } from '@/shared/components/illustrations';
import { getEmployeeRoleDisplayName } from '@/shared/utils/role-utils';

interface UserSelectorChoiceListProps {
  employees: UserSelectorEmployee[];
  selectedEmployees: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  multiSelect?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  className?: string;
  maxHeight?: string;
  showSelectAll?: boolean;
  columns?: 1 | 2 | 3;
}

export const UserSelectorChoiceList: React.FC<UserSelectorChoiceListProps> = ({
  employees,
  selectedEmployees,
  onSelectionChange,
  multiSelect,
  searchable = true,
  searchPlaceholder,
  className = '',
  maxHeight = 'max-h-96',
  showSelectAll = true,
  columns = 2,
}) => {
  const { t } = useTranslation('userSelector');
  const { t: tCommon } = useTranslation('common');
  const [searchTerm, setSearchTerm] = useState('');

  // Filter employees based on search
  const filteredEmployees = useMemo(() => {
    if (!searchTerm.trim()) return employees;
    return employees.filter((employee) =>
      employee.fullName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [employees, searchTerm]);

  // Transform employees to ChooseListInput options
  const options = useMemo(() => {
    return filteredEmployees.map((employee) => ({
      id: employee.id,
      title: employee.fullName,
      description: employee.role
        ? getEmployeeRoleDisplayName(employee.role, tCommon)
        : undefined,
      disabled: !employee.isActive,
      isTeamMember: employee.isTeamMember,
      icon: (
        <UserSelectorAvatar
          imageUrl={employee.imageUrl}
          fullName={employee.fullName}
          size="md"
        />
      ),
    }));
  }, [filteredEmployees, tCommon]);

  const handleSelectAll = () => {
    const enabledEmployeeIds = filteredEmployees
      .filter((emp) => emp.isActive)
      .map((emp) => emp.id);
    onSelectionChange(enabledEmployeeIds);
  };

  const handleDeselectAll = () => {
    onSelectionChange([]);
  };

  const enabledEmployees = filteredEmployees.filter((emp) => emp.isActive);
  const allEnabledSelected =
    enabledEmployees.length > 0 &&
    enabledEmployees.every((emp) => selectedEmployees.includes(emp.id));

  return (
    <Card
      className={cn(
        'border-none shadow-none bg-transparent flex flex-col gap-4',
        className
      )}
    >
      {/* Search */}
      {searchable && (
        <CardHeader className="py-2">
          <div className="relative group">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground/60 group-focus-within:text-primary transition-colors rtl:right-4" />
            <Input
              type="text"
              placeholder={searchPlaceholder || t('searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 py-6 rounded-xl bg-white shadow-sm hover:shadow-md hover:border-primary/30 transition-all duration-200 rtl:pr-12"
            />
          </div>
        </CardHeader>
      )}

      {/* Select All Controls for multi-select */}
      {multiSelect && showSelectAll && enabledEmployees.length > 0 && (
        <CardHeader className="py-4 mb-4 rounded-xl bg-accent/30">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">
              {allEnabledSelected ? t('allSelected') : t('selectOptions')}
            </span>
            <div className="flex gap-2">
              {!allEnabledSelected && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSelectAll}
                  className="h-auto px-3 text-sm font-medium text-primary rounded-lg hover:bg-primary/10"
                >
                  {t('selectAll')}
                </Button>
              )}
              {selectedEmployees.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDeselectAll}
                  className="h-auto px-3 text-sm font-medium text-muted-foreground rounded-lg hover:text-foreground hover:bg-accent"
                >
                  {t('clearAll')}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
      )}

      <CardContent className={cn('overflow-y-auto p-4', maxHeight)}>
        {filteredEmployees.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-12">
            <EmptyStateIllustration className="text-primary mb-6" />
            <p className="text-base font-semibold text-foreground mb-2">
              {searchTerm ? 'No results found' : 'No team members available'}
            </p>
            <p className="text-sm text-muted-foreground max-w-xs">
              {searchTerm
                ? 'Try adjusting your search terms or filters'
                : 'There are no team members to display at this time'}
            </p>
          </div>
        ) : multiSelect ? (
          <ChooseListInput
            options={options}
            value={selectedEmployees}
            onChange={(value) => {
              onSelectionChange(value);
            }}
            columns={columns}
            variant="card"
            multiSelect={true}
            className="w-full capitalize"
          />
        ) : (
          <ChooseListInput
            options={options}
            value={selectedEmployees[0] || ''}
            onChange={(value) => {
              onSelectionChange([value]);
            }}
            columns={columns}
            variant="card"
            multiSelect={false}
            className="w-full capitalize"
          />
        )}
      </CardContent>
    </Card>
  );
};
